// client/src/components/AlarmsView/FilterBuilder.jsx
// AND/OR filter group editor. Recursive: groups can contain rules AND other
// groups. The JSON it emits is exactly the shape the server's filterCompiler
// expects, so what you see is what gets evaluated.
//
//   value:    { combinator: 'AND'|'OR', rules: [Rule | Group, ...] }
//   Rule:     { field, operator, value }
//   Group:    same shape as the top-level
//
// Operators with array values (IN, NOT IN, BETWEEN) get a comma-separated
// text input that's split on save. IS NULL / IS NOT NULL have no value field.

import { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const OPS = ['=', '!=', '>', '<', '>=', '<=', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL', 'LIKE', 'BETWEEN'];
const NO_VALUE_OPS  = new Set(['IS NULL', 'IS NOT NULL']);
const ARRAY_OPS     = new Set(['IN', 'NOT IN']);
const PAIR_OPS      = new Set(['BETWEEN']);

const C = {
  cardBg: '#0f172a', border: '#1e293b', input: '#020617',
  text: '#e5e7eb', muted: '#9ca3af', accent: '#a78bfa', danger: '#ef4444'
};

const ipt = {
  padding: '6px 8px', background: C.input, border: `1px solid ${C.border}`,
  borderRadius: 4, color: C.text, fontSize: 12, outline: 'none'
};

function isGroup(node) { return node && typeof node === 'object' && Array.isArray(node.rules); }

// Convert a stored Rule.value into the text the input should show.
function toInputValue(rule) {
  if (NO_VALUE_OPS.has(rule.operator)) return '';
  if (ARRAY_OPS.has(rule.operator) || PAIR_OPS.has(rule.operator)) {
    return Array.isArray(rule.value) ? rule.value.join(', ') : '';
  }
  return rule.value ?? '';
}

// Convert the text input back into the value the server expects.
function fromInputValue(operator, text) {
  if (NO_VALUE_OPS.has(operator)) return null;
  if (ARRAY_OPS.has(operator)) {
    return String(text).split(',').map(s => s.trim()).filter(s => s !== '');
  }
  if (PAIR_OPS.has(operator)) {
    const parts = String(text).split(',').map(s => s.trim()).filter(s => s !== '');
    return parts.slice(0, 2);
  }
  return text;
}

export default function FilterBuilder({ value, onChange, fields = [] }) {
  const group = useMemo(() => value && isGroup(value)
    ? value
    : { combinator: 'AND', rules: [] }, [value]);

  return (
    <GroupNode group={group} onChange={onChange} fields={fields} depth={0} />
  );
}

function GroupNode({ group, onChange, fields, depth }) {
  const setCombinator = (combinator) => onChange({ ...group, combinator });
  const setRules = (rules) => onChange({ ...group, rules });

  const addRule  = () => setRules([...group.rules, { field: fields[0] || '', operator: '=', value: '' }]);
  const addGroup = () => setRules([...group.rules, { combinator: 'AND', rules: [] }]);
  const updateAt = (i, next) => setRules(group.rules.map((r, idx) => idx === i ? next : r));
  const removeAt = (i)       => setRules(group.rules.filter((_, idx) => idx !== i));

  return (
    <div style={{
      padding: 10,
      background: depth === 0 ? 'transparent' : C.cardBg,
      border: `1px dashed ${C.border}`,
      borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 8
    }}>
      {/* Header: combinator + add buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: C.muted, marginRight: 4 }}>Match</span>
        {['AND', 'OR'].map(c => (
          <button key={c}
            onClick={() => setCombinator(c)}
            style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
              background: group.combinator === c ? C.accent : C.input,
              color:      group.combinator === c ? '#fff'    : C.muted,
              border: `1px solid ${group.combinator === c ? C.accent : C.border}`
            }}>{c}</button>
        ))}
        <span style={{ flex: 1 }} />
        <button onClick={addRule}
          style={{ ...ipt, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Plus size={11} /> Rule
        </button>
        <button onClick={addGroup}
          style={{ ...ipt, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Plus size={11} /> Group
        </button>
      </div>

      {group.rules.length === 0 && (
        <div style={{ fontSize: 11, color: C.muted, padding: '4px 0', fontStyle: 'italic' }}>
          No filter — alarm evaluates on every row.
        </div>
      )}

      {/* Rules + nested groups */}
      {group.rules.map((node, i) => isGroup(node) ? (
        <div key={i} style={{ position: 'relative' }}>
          <button onClick={() => removeAt(i)}
            title="Remove group"
            style={{
              position: 'absolute', top: 6, right: 6, zIndex: 1,
              padding: 4, background: 'transparent', border: `1px solid ${C.border}`,
              borderRadius: 4, color: C.danger, cursor: 'pointer'
            }}><Trash2 size={11} /></button>
          <GroupNode group={node} onChange={(g) => updateAt(i, g)} fields={fields} depth={depth + 1} />
        </div>
      ) : (
        <RuleRow key={i} rule={node} fields={fields}
                 onChange={(r) => updateAt(i, r)}
                 onRemove={() => removeAt(i)} />
      ))}
    </div>
  );
}

function RuleRow({ rule, fields, onChange, onRemove }) {
  const setField    = (field)    => onChange({ ...rule, field });
  const setOperator = (operator) => {
    // When operator class changes (no-value ↔ value, scalar ↔ array), normalise value.
    let value = rule.value;
    if (NO_VALUE_OPS.has(operator)) value = null;
    else if (ARRAY_OPS.has(operator) && !Array.isArray(value)) value = [];
    else if (!ARRAY_OPS.has(operator) && Array.isArray(value))  value = value.join(', ');
    onChange({ ...rule, operator, value });
  };
  const setValue = (text) => onChange({ ...rule, value: fromInputValue(rule.operator, text) });

  const hideValue = NO_VALUE_OPS.has(rule.operator);
  const placeholder = ARRAY_OPS.has(rule.operator) ? 'val1, val2, val3'
                    : PAIR_OPS.has(rule.operator)  ? 'low, high'
                    : 'value';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 0.8fr 1.2fr auto',
      gap: 6, padding: 6, background: C.input, border: `1px solid ${C.border}`, borderRadius: 4
    }}>
      <select value={rule.field} onChange={(e) => setField(e.target.value)} style={ipt}>
        <option value="">Field…</option>
        {fields.map(f => <option key={f} value={f}>{f}</option>)}
        {/* Allow custom field that isn't in the schema dropdown */}
        {rule.field && !fields.includes(rule.field) && (
          <option key={rule.field} value={rule.field}>{rule.field}</option>
        )}
      </select>
      <select value={rule.operator} onChange={(e) => setOperator(e.target.value)} style={ipt}>
        {OPS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {hideValue ? (
        <div style={{ ...ipt, opacity: 0.5, fontStyle: 'italic' }}>—</div>
      ) : (
        <input
          type="text"
          value={toInputValue(rule)}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          style={ipt}
        />
      )}
      <button onClick={onRemove} title="Remove"
        style={{ ...ipt, cursor: 'pointer', color: C.danger, borderColor: C.border, padding: '6px 8px' }}>
        <Trash2 size={12} />
      </button>
    </div>
  );
}
